import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { fileUrl } from '../utils/fileUrl';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Link as LinkIcon, Copy, Trash2, CheckCircle, XCircle, Clock, UserCheck, FileText, Car, Phone, Loader2 } from 'lucide-react';

const statusBadge = (status) => {
  const styles = {
    pending: 'bg-gold-100 text-gold-800',
    submitted: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    revoked: 'bg-gray-100 text-gray-600'
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.revoked}`}>{status}</span>;
};

const DocLink = ({ url, label }) => {
  if (!url) return null;
  const full = fileUrl(url);
  const isPdf = url.toLowerCase().endsWith('.pdf');
  return (
    <a href={full} target="_blank" rel="noreferrer" className="block">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {isPdf ? (
        <div className="flex items-center gap-2 text-blue-600 text-sm border rounded-lg p-3 hover:bg-blue-50">
          <FileText className="w-4 h-4" /> View PDF
        </div>
      ) : (
        <img src={full} alt={label} className="w-full h-32 object-cover rounded-lg border hover:opacity-90" />
      )}
    </a>
  );
};

export default function AdminDriverApplications() {
  const [applications, setApplications] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = async () => {
    try {
      const [appsRes, invitesRes] = await Promise.all([
        axios.get(`${API}/admin/driver-applications`),
        axios.get(`${API}/admin/driver-invites`)
      ]);
      setApplications(appsRes.data);
      setInvites(invitesRes.data);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const createInvite = async (sendEmail) => {
    if (sendEmail && !inviteEmail) return toast.error('Enter an email to send the invite');
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/driver-invites`, {
        email: inviteEmail || null,
        name: inviteName || null,
        send_email: sendEmail
      });
      await navigator.clipboard.writeText(res.data.invite_url).catch(() => {});
      toast.success(sendEmail
        ? (res.data.email_sent ? `Invite emailed to ${inviteEmail}` : 'Invite created but email failed to send — link copied')
        : 'Invite link created & copied to clipboard');
      setInviteName('');
      setInviteEmail('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invite');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    await axios.delete(`${API}/admin/driver-invites/${id}`);
    toast.success('Invite revoked');
    fetchData();
  };

  const review = async (userId, action, reason = '') => {
    try {
      await axios.put(`${API}/admin/driver-applications/${userId}`, { action, reason });
      toast.success(action === 'approve' ? 'Driver approved — activate them in Manage Drivers when ready' : 'Application rejected — email sent');
      setRejectingId(null);
      setRejectReason('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update application');
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Loading...</div>;

  const pending = applications.filter((a) => a.approval_status === 'pending');
  const reviewed = applications.filter((a) => a.approval_status !== 'pending');

  return (
    <div className="space-y-8" data-testid="driver-applications-page">
      <div>
        <h1 className="text-2xl font-bold">Driver Applications</h1>
        <p className="text-gray-500 text-sm">Invite drivers, review their documents, and approve or reject applications.</p>
      </div>

      {/* Invite Section */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Invite a Driver</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Driver Name (optional)</Label>
              <Input data-testid="invite-name-input" value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="John Smith" />
            </div>
            <div>
              <Label>Driver Email</Label>
              <Input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="driver@email.com" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="send-invite-email-button" onClick={() => createInvite(true)} disabled={sending} className="bg-black text-white hover:bg-gray-800">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Send Email Invite
            </Button>
            <Button data-testid="generate-invite-link-button" onClick={() => createInvite(false)} disabled={sending} variant="outline">
              <LinkIcon className="w-4 h-4 mr-2" /> Generate Link Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending Applications */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-gold-600" /> Pending Review ({pending.length})</h2>
        {pending.length === 0 ? (
          <p className="text-gray-400 text-sm">No applications awaiting review.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {pending.map((app) => (
              <Card key={app.id} data-testid={`application-card-${app.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-4">
                    {app.profile_photo_url ? (
                      <img src={fileUrl(app.profile_photo_url)} alt={app.name} className="w-16 h-16 rounded-full object-cover border" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center"><UserCheck className="w-6 h-6 text-gray-400" /></div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold">{app.name}</h3>
                      <p className="text-sm text-gray-500">{app.email}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {app.phone || '—'}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1"><Car className="w-3 h-3" /> {app.car_type} • {app.license_plate}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <DocLink url={app.chauffeur_license_url} label="Chauffeur's License" />
                    <DocLink url={app.insurance_doc_url} label="Insurance" />
                  </div>
                  {rejectingId === app.id ? (
                    <div className="space-y-2">
                      <Input data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason (included in email, optional)" />
                      <div className="flex gap-2">
                        <Button data-testid="confirm-reject-button" onClick={() => review(app.id, 'reject', rejectReason)} variant="destructive" size="sm">Confirm Reject</Button>
                        <Button onClick={() => setRejectingId(null)} variant="outline" size="sm">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button data-testid={`approve-button-${app.id}`} onClick={() => review(app.id, 'approve')} className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button data-testid={`reject-button-${app.id}`} onClick={() => setRejectingId(app.id)} variant="destructive" className="flex-1">
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invites List */}
      <Card>
        <CardHeader><CardTitle className="text-base">Sent Invites</CardTitle></CardHeader>
        <CardContent>
          {invites.length === 0 ? (
            <p className="text-gray-400 text-sm">No invites sent yet.</p>
          ) : (
            <div className="divide-y">
              {invites.map((inv) => (
                <div key={inv.id} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`invite-row-${inv.id}`}>
                  <div>
                    <p className="font-medium text-sm">{inv.name || inv.email || 'Open invite link'}</p>
                    <p className="text-xs text-gray-500">{inv.email || 'No email — shared manually'} • {new Date(inv.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(inv.status)}
                    {inv.status === 'pending' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => copyLink(inv.invite_url)} data-testid={`copy-invite-${inv.id}`}><Copy className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => revokeInvite(inv.id)} data-testid={`revoke-invite-${inv.id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reviewed */}
      {reviewed.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Reviewed Applications</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y">
              {reviewed.map((app) => (
                <div key={app.id} className="py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {app.profile_photo_url && <img src={fileUrl(app.profile_photo_url)} alt={app.name} className="w-9 h-9 rounded-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
                    <div>
                      <p className="font-medium text-sm">{app.name}</p>
                      <p className="text-xs text-gray-500">{app.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(app.approval_status)}
                    {app.approval_status === 'rejected' && (
                      <Button size="sm" variant="outline" onClick={() => review(app.id, 'approve')} data-testid={`re-approve-${app.id}`}>Approve Instead</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
